import { createContext, type CSSProperties, useContext } from 'react';

import type { AssetDelivery, Campaign } from '../api.ts';
import { FUNNEL_LABEL } from '../BriefDetailsFields.tsx';
import { attentionReason } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { Link } from '../router.tsx';
import { since } from '../schedule.ts';
import { Badge, Loader, StatusBadge } from '../ui.tsx';
import * as wording from '../wording.ts';
import { CardMenu } from './CardMenu.tsx';
import { CAMPAIGN_COLOR_VAR, cardLine, dueChip, postChip, runWindowChip } from './lines.ts';
import { type BoardCard, cardHref, cardTitle, needsYou } from './model.ts';
import { CARD_DATA_TYPE } from './transitions.ts';
import { PrimaryActionButton } from './PrimaryActionButton.tsx';

/**
 * The one card the keys act on (US-027). It alone is in the tab order; the
 * others are reached with the keys, a click, or the drawer. Without a provider
 * (a card drawn off the board) every card is tabbable, as before.
 */
export const CardFocusContext = createContext<{
  readonly focusedId: string | null;
  readonly onFocus: (cardId: string) => void;
} | null>(null);

/**
 * The cards selected for a bulk action (US-028). Ctrl/Cmd+click toggles a
 * card, Shift+click takes the range from the last one toggled in its column.
 * Without a provider a click only opens the card.
 */
export const CardSelectionContext = createContext<{
  readonly selected: ReadonlySet<string>;
  readonly onSelect: (cardId: string, mode: 'toggle' | 'range') => void;
} | null>(null);

/**
 * One card, as Design reference § Card anatomy lays it out (US-015): the
 * campaign's colour down its left edge, the poster, the title, a meta line,
 * the line saying what is happening, its badges, and the spend and age at the
 * foot. Everything is read from the card itself and the clock the board
 * rendered with, so two cards drawn in one pass agree about "now".
 */
export function Card({
  card,
  now,
  campaigns,
  poster,
  campaignLane = false,
  open = false,
  onOpen,
}: {
  readonly card: BoardCard;
  readonly now: number;
  readonly campaigns: ReadonlyMap<string, Campaign>;
  /** The poster's delivery; absent with Thumbnails off or nothing to show. */
  readonly poster: AssetDelivery | undefined;
  /** Grouped by campaign: the lane already names it, so the chip is left off. */
  readonly campaignLane?: boolean;
  /** Its drawer is the one open (US-018). */
  readonly open?: boolean;
  /** Opens its drawer: a click anywhere but a link or button on it, or Enter on it. */
  readonly onOpen?: () => void;
}) {
  const content = card.type === 'content' ? card.content : null;
  const idea = card.type === 'idea' ? card.idea : null;
  const href = cardHref(card);
  const focus = useContext(CardFocusContext);
  const focused = focus !== null && focus.focusedId === card.cardId;
  const selection = useContext(CardSelectionContext);
  const selected = selection?.selected.has(card.cardId) ?? false;

  const campaignId = content?.campaignId ?? idea?.campaignId ?? null;
  const campaign = campaignId === null ? undefined : campaigns.get(campaignId);
  const blockedReason = content?.blockedReason ?? idea?.blockedReason ?? null;
  const attention = needsYou(card);
  const sub = cardLine(card, now);
  // An idea's reason is its line; a content's is the one the rest of the app gives.
  const reason = content === null ? sub.text : attentionReason(content);

  const kind = content?.kind ?? idea?.kind ?? null;
  const seconds = content?.targetSeconds ?? idea?.targetSeconds ?? null;
  const formats = content?.formats ?? idea?.formats ?? [];
  const tone = content?.tone ?? idea?.tone ?? null;
  const funnel = (content ?? idea)?.briefDetails.funnelStages ?? [];
  const placed = content?.briefDetails.placed ?? false;
  // A rejected content is not used (rejected-status US-007): nothing on its
  // card says it is due, neither the publish-by date nor a planned post.
  const rejected = content?.status === 'rejected';
  const due = dueChip(content?.dueOn ?? idea?.dueOn ?? null, placed || rejected, now);
  const runWindow = runWindowChip(idea?.runWindow ?? null, now);
  const checklist = content?.checklist ?? [];
  const posts = card.type === 'content' ? card.posts.filter((post) => !rejected || post.status === 'posted') : [];
  const spent = content?.spentUsd ?? idea?.costUsd ?? 0;
  const updatedAt = content?.updatedAt ?? idea?.workedAt ?? idea?.createdAt ?? null;

  const className = [
    'board-card',
    campaign === undefined ? '' : 'board-card--campaign',
    attention ? 'board-card--attention' : '',
    blockedReason === null ? '' : 'board-card--blocked',
    open ? 'board-card--open' : '',
    focused ? 'board-card--focused' : '',
    selected ? 'board-card--selected' : '',
  ]
    .filter((name) => name !== '')
    .join(' ');
  const style =
    campaign === undefined
      ? undefined
      : ({ '--board-card-edge': CAMPAIGN_COLOR_VAR[campaign.color] } as CSSProperties);

  return (
    <article
      className={className}
      style={style}
      data-card-id={card.cardId}
      data-column={card.column}
      tabIndex={focus === null || focused ? 0 : -1}
      aria-current={open ? 'true' : undefined}
      aria-selected={selection === null ? undefined : selected}
      // Native drag and drop (US-025): the board reads what a drop means from
      // `board/transitions.ts`. A drag begun on the title or the poster still
      // carries the card, and shows the card rather than a link.
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData(CARD_DATA_TYPE, card.cardId);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setDragImage(event.currentTarget, 16, 16);
      }}
      onFocus={() => {
        if (!focused) focus?.onFocus(card.cardId);
      }}
      // Shift+click would otherwise select the text of every card in between.
      onMouseDown={(event) => {
        if (event.shiftKey && selection !== null) event.preventDefault();
      }}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('a, button, input, select, textarea, label') !== null) return;
        if (selection !== null && (event.ctrlKey || event.metaKey || event.shiftKey)) {
          selection.onSelect(card.cardId, event.shiftKey ? 'range' : 'toggle');
          return;
        }
        onOpen?.();
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' || event.target !== event.currentTarget) return;
        event.preventDefault();
        onOpen?.();
      }}
    >
      <span className="board-card__grab" aria-hidden="true">
        <Icon name="grabber" />
      </span>
      <CardMenu card={card} />
      {poster !== undefined && (
        <div className="board-card__poster">
          <img src={poster.url} alt="" loading="lazy" decoding="async" />
        </div>
      )}
      <h3 className="board-card__title">
        <Link className="board-card__link" href={href}>
          {cardTitle(card)}
        </Link>
      </h3>
      {(kind !== null || formats.length > 0 || tone !== null) && (
        <p className="board-card__meta">
          {kind !== null && (
            <span className="board-card__kind" title={kind === 'video' ? 'Video' : 'Image'}>
              <Icon name={kind === 'video' ? 'device-camera-video' : 'image'} />
            </span>
          )}
          {kind === 'video' && seconds !== null && <span>{wording.cardSeconds(seconds)}</span>}
          {formats.length > 0 && <span>{formats.join(', ')}</span>}
          {tone !== null && <span>{tone}</span>}
        </p>
      )}
      <div className="board-card__line" {...(sub.title === undefined ? {} : { title: sub.title })}>
        {sub.loader ? <Loader size="sm" label={sub.text} /> : sub.text}
      </div>
      <div className="board-card__badges">
        {rejected && <StatusBadge status="rejected" />}
        {attention && reason !== null && (
          <Badge tone="danger">{reason}</Badge>
        )}
        {funnel.map((stage) => (
          <Badge key={stage}>{FUNNEL_LABEL[stage]}</Badge>
        ))}
        {blockedReason !== null && (
          <Badge tone="danger" title={blockedReason}>
            {wording.BOARD_BLOCKED}
          </Badge>
        )}
        {due !== null && (
          <span className={`badge badge--${due.tone} board-card__chip`}>
            <Icon name="calendar" />
            {due.text}
          </span>
        )}
        {runWindow !== null && (
          <span className={`badge badge--${runWindow.tone} board-card__chip`} title={runWindow.title}>
            <Icon name="calendar" />
            {runWindow.text}
          </span>
        )}
        {checklist.length > 0 && (
          <span
            className="badge badge--neutral board-card__chip"
            title={wording.checklistTitle(checklist.filter((item) => item.done).length, checklist.length)}
          >
            <Icon name="tasklist" />
            {wording.checklistProgress(checklist.filter((item) => item.done).length, checklist.length)}
          </span>
        )}
        {posts.map((post) => {
          const chip = postChip(post, content?.status === 'delivered', now);
          return (
            <span
              key={post.id}
              className={`badge badge--${chip.posted ? 'done' : chip.tone} board-card__chip`}
              {...(chip.title === undefined ? {} : { title: chip.title })}
            >
              {chip.text}
              {chip.posted && <span aria-label={wording.BOARD_POSTED}> ✓</span>}
            </span>
          );
        })}
        {placed && card.column !== 'posted' && <Badge tone="done">{wording.BOARD_PLACED}</Badge>}
        {campaign !== undefined && !campaignLane && (
          <span className="badge badge--neutral board-card__chip board-card__campaign">{campaign.name}</span>
        )}
      </div>
      <PrimaryActionButton card={card} variant="card" />
      {(spent > 0 || updatedAt !== null) && (
        <footer className="board-card__footer">
          {spent > 0 && <span>{wording.dollars(spent)}</span>}
          {updatedAt !== null && <span>{since(updatedAt, now)}</span>}
        </footer>
      )}
    </article>
  );
}
